import { useCallback, useMemo } from 'react';

import PrecisionService from '@core/services/PrecisionService';

import { mapToPlanCoversLovs } from '../helpers';
import { useGetPlanCovers } from '../proposal.slice';
import { IPolicyCoverForm } from '../types';

export function usePlanCoversLovs(
  planID: string,
  rows: IPolicyCoverForm[],
  rate?: number,
  skipCall = false
) {
  const { data, loading } = useGetPlanCovers({
    variables: { planID },
    skip: !planID || skipCall,
    fetchPolicy: 'cache-and-network'
  });

  const allOptions = useMemo(() => mapToPlanCoversLovs(data), [data]);
  const rawItems =
    data?.planConfig?.getPlanPlanCovers?.items.map((item) => ({
      ...item,
      premiumPercentage:
        item.premiumType?.toUpperCase() === 'PERCENTAGE'
          ? PrecisionService.round(item.premiumPercentage * 100, 3)
          : 0,
      premiumAmount:
        item.premiumType?.toUpperCase() === 'AMOUNT'
          ? PrecisionService.round(item.premiumAmount * rate, 3)
          : 0,
      sumInsured: PrecisionService.round(item.sumInsured * rate, 3),
      excessOnClaimAmount: PrecisionService.round(
        item.excessOnClaimAmount * rate,
        3
      ),
      excessOnClaimPercentage: PrecisionService.round(
        PrecisionService.multiplyBy100(item.excessOnClaimPercentage),
        3
      )
    })) ?? [];

  const selected = useMemo(
    () => (rows ?? []).map((r) => r?.policyCover || ''),
    [rows]
  );

  const filteredForIndex = useCallback(
    (rowIndex: number) => {
      const f = { ...allOptions };
      selected.forEach((id, i) => {
        if (i !== rowIndex && id) delete f[id];
      });

      return f;
    },
    [allOptions, selected]
  );

  const hasUnused = useMemo(() => {
    const picked = new Set(selected.filter(Boolean));

    return Object.keys(allOptions).some((id) => !picked.has(id));
  }, [allOptions, selected]);

  return { rawItems, filteredForIndex, hasUnused, allOptions, loading };
}
